// "Unidades" de la carga masiva: cada unidad es UN producto a crear, con 1+
// imágenes. Es la única fuente de verdad para lo que se muestra en la lista
// del modal "Subir varios productos" Y para las tareas que arma `saveAll` —
// si se calcularan por separado, lo que ves y lo que se crea divergirían en
// el primer cambio.
//
// La base de agrupamiento depende del toggle "Usar nombres de archivo":
//   • apagado → cada item es su propia unidad (key = id del item);
//   • prendido → grupos por stem del filename (groupItemsByStem, key = stem).
// Encima de esa base se aplican los merges manuales (`attachTo`): el usuario
// puede "añadir esta imagen a otro producto" tocándola en la lista, y eso se
// registra como itemId → key de la unidad destino. Los archivos añadidos van
// al FINAL de la unidad destino: la imagen principal (la primera) del
// destino no cambia por recibir imágenes.
//
// Las fotos que vinieron de una carpeta (un .zip o una carpeta soltada,
// ADR-2026-10-16-subir-varios-con-zip-y-carpetas) forman SU unidad con el toggle
// prendido o apagado: la carpeta es la agrupación que eligió el usuario, y por
// stem los `1.jpg` de carpetas distintas se mezclarían.

import { groupItemsByStem } from "./groupFilesByStem";

/** Prefijo de la key de una unidad armada por carpeta. */
export const PREFIJO_CARPETA = "carpeta:";

export const esUnidadDeCarpeta = (key: string): boolean =>
  key.startsWith(PREFIJO_CARPETA);

/** La carpeta de la que vino una foto: `key` única por lote + nombre visible. */
export interface CarpetaDeItem {
  key: string;
  nombre: string;
}

export interface BulkUnit<T> {
  /** Estable entre renders: id del item host (modo plano), key del stem
   *  (modo "usar nombres de archivo") o `carpeta:<key>` (vino de una carpeta). */
  key: string;
  /** Nombre derivado del filename (modo por stem) o de la carpeta; null en
   *  plano, donde el nombre vive en el propio item host. */
  baseName: string | null;
  /** Imágenes de la unidad. La primera es la principal. */
  files: T[];
}

/**
 * Calcula las unidades a partir de los items, el modo y los merges manuales.
 *
 * Un `attachTo` que apunta a una unidad que ya no existe (el host se borró
 * por un camino que no limpió el mapa) NO tira el archivo: el item vuelve a
 * ser su propia unidad. Perder archivos en silencio es el único resultado
 * prohibido acá.
 */
export function computeUnits<T extends { id: string }>(
  items: T[],
  byStem: boolean,
  getName: (item: T) => string,
  attachTo: Record<string, string>,
  getCarpeta: (item: T) => CarpetaDeItem | null | undefined = () => null,
): BulkUnit<T>[] {
  const hosts: T[] = [];
  const attached: T[] = [];
  const deCarpeta = new Map<string, BulkUnit<T>>();
  for (const it of items) {
    // hasOwnProperty.call y no Object.hasOwn: la lib del tsconfig es < es2022.
    if (Object.prototype.hasOwnProperty.call(attachTo, it.id)) {
      attached.push(it);
      continue;
    }
    const carpeta = getCarpeta(it);
    if (!carpeta) {
      hosts.push(it);
      continue;
    }
    const key = PREFIJO_CARPETA + carpeta.key;
    const unit = deCarpeta.get(key);
    if (unit) unit.files.push(it);
    else deCarpeta.set(key, { key, baseName: carpeta.nombre, files: [it] });
  }

  const base: BulkUnit<T>[] = byStem
    ? groupItemsByStem(hosts, getName).map((g) => ({
        key: g.key,
        baseName: g.baseName,
        files: [...g.files],
      }))
    : hosts.map((it) => ({ key: it.id, baseName: null, files: [it] }));

  // Las unidades de carpeta se intercalan con las otras por su primera foto: la
  // lista queda en el orden en que se cargaron las cosas.
  const posicion = new Map(items.map((it, i) => [it.id, i]));
  const primera = (u: BulkUnit<T>) => posicion.get(u.files[0].id) ?? 0;
  const carpetas = [...deCarpeta.values()];
  const units: BulkUnit<T>[] = [];
  let b = 0;
  let c = 0;
  while (b < base.length || c < carpetas.length) {
    if (c >= carpetas.length || (b < base.length && primera(base[b]) < primera(carpetas[c])))
      units.push(base[b++]);
    else units.push(carpetas[c++]);
  }

  const byKey = new Map(units.map((u) => [u.key, u]));

  const asOwnUnit = (it: T) => {
    const carpeta = getCarpeta(it);
    if (carpeta) {
      // Huérfano de una carpeta: vuelve a su carpeta (o la rearma).
      const key = PREFIJO_CARPETA + carpeta.key;
      const existing = byKey.get(key);
      if (existing) existing.files.push(it);
      else {
        const unit = { key, baseName: carpeta.nombre, files: [it] };
        units.push(unit);
        byKey.set(key, unit);
      }
    } else if (byStem) {
      // Se reinserta con el mismo algoritmo de la base: si su stem coincide
      // con una unidad existente, cae ahí; si no, unidad nueva.
      const g = groupItemsByStem([it], getName)[0];
      const existing = byKey.get(g.key);
      if (existing) {
        existing.files.push(it);
      } else {
        const unit = { key: g.key, baseName: g.baseName, files: [it] };
        units.push(unit);
        byKey.set(unit.key, unit);
      }
    } else {
      const unit = { key: it.id, baseName: null, files: [it] };
      units.push(unit);
      byKey.set(unit.key, unit);
    }
  };

  for (const it of attached) {
    const target = byKey.get(attachTo[it.id]);
    if (target) target.files.push(it);
    else asOwnUnit(it);
  }

  return units;
}

// ── "Agrupar con IA" ─────────────────────────────────────────────────────

/** Un grupo como lo devuelve el backend (POST /v1/products/group-images):
 *  ids de items del front (el primero es la portada) + nombre sugerido. */
export interface GrupoIaRespuesta {
  ids: string[];
  nombre: string;
}

/**
 * Convierte los grupos de la IA en el mapa `attachTo` (modo plano: host = el
 * primer id del grupo) + nombres sugeridos por host. Cinturón defensivo del
 * lado del cliente, aunque el backend ya sanea: ids desconocidos se ignoran,
 * repetidos gana la primera aparición, y el excedente sobre `maxPorGrupo`
 * queda como unidad propia — nunca se pierde una imagen.
 */
export function attachToDesdeGrupos(
  idsExistentes: string[],
  grupos: GrupoIaRespuesta[],
  maxPorGrupo: number,
): { attachTo: Record<string, string>; nombres: Record<string, string> } {
  const existentes = new Set(idsExistentes);
  const usados = new Set<string>();
  const attachTo: Record<string, string> = {};
  const nombres: Record<string, string> = {};
  for (const g of grupos) {
    const ids = g.ids.filter((id) => existentes.has(id) && !usados.has(id));
    ids.forEach((id) => usados.add(id));
    const efectivos = ids.slice(0, maxPorGrupo);
    const host = efectivos[0];
    if (!host) continue;
    for (const id of efectivos.slice(1)) attachTo[id] = host;
    const nombre = (g.nombre ?? "").trim();
    if (nombre) nombres[host] = nombre;
  }
  return { attachTo, nombres };
}

/**
 * El límite de la carga cuenta PRODUCTOS, no fotos. Deja entrar los bloques en
 * orden mientras las unidades resultantes no pasen `limite`: una carpeta es un
 * bloque (entra entera o no entra), una foto suelta es un bloque de uno. Una
 * suelta que cae en un grupo existente (modo por nombre) no suma producto, así
 * que entra aunque se esté en el límite.
 */
export function entrarEnElLimite<T>(
  previos: T[],
  bloques: T[][],
  contarUnidades: (items: T[]) => number,
  limite: number,
): { aceptados: T[]; rechazados: T[][] } {
  const aceptados: T[] = [];
  const rechazados: T[][] = [];
  for (const bloque of bloques) {
    if (contarUnidades([...previos, ...aceptados, ...bloque]) <= limite)
      aceptados.push(...bloque);
    else rechazados.push(bloque);
  }
  return { aceptados, rechazados };
}

/**
 * Aplica la respuesta de "Agrupar con IA" SIN pisar lo que la IA no miró: las uniones
 * hechas a mano de las fotos que no se le mandaron (las de una carpeta, o una suelta ya
 * sumada a una carpeta) se conservan. Si una de esas apuntaba a una suelta que la IA metió
 * en otro producto, sigue a ese producto. Sin esto, juntar la carpeta A con la B y después
 * agrupar sueltas con IA volvía a separar A (review Greptile #184).
 *
 * La IA sólo ve las fotos que se le mandaron, así que no sabe cuántas arrastra cada una:
 * si un grupo suyo pasa de `max` con las que lo siguen, se deshacen sus agregados (primero
 * los que traen uniones a mano, del último al primero) hasta que entre. Las uniones del
 * usuario quedan intactas y el grupo de la IA se achica: nunca queda un producto con fotos
 * que `saveAll` no subiría (review Greptile #184, segunda vuelta).
 */
export function combinarConLaIa(
  previo: Record<string, string>,
  deLaIa: Record<string, string>,
  idsMandados: Set<string>,
  max: number,
): Record<string, string> {
  const r = { ...deLaIa };
  // Foto de la IA → las conservadas que la siguen a su nuevo producto.
  const seguidores = new Map<string, string[]>();
  for (const [id, destino] of Object.entries(previo)) {
    if (idsMandados.has(id)) continue;
    const nuevoDestino = deLaIa[destino];
    r[id] = nuevoDestino ?? destino;
    if (nuevoDestino) seguidores.set(destino, [...(seguidores.get(destino) ?? []), id]);
  }
  const tamano = (host: string) =>
    1 + Object.values(r).filter((d) => d === host).length;
  for (const host of new Set(Object.values(deLaIa))) {
    const agregados = Object.keys(deLaIa).filter((id) => deLaIa[id] === host).reverse();
    const orden = [
      ...agregados.filter((id) => seguidores.has(id)),
      ...agregados.filter((id) => !seguidores.has(id)),
    ];
    for (const id of orden) {
      if (tamano(host) <= max) break;
      // Vuelve a ser su propio producto, con las que la seguían.
      delete r[id];
      for (const f of seguidores.get(id) ?? []) r[f] = id;
    }
  }
  return r;
}
